<!-- EntityDetail.vue -->
<template>
  <div id="entity-detail" class="component stk-p-8">
    <LoaderWithText
      v-if="loading"
      class="stk-py-2_5"
    >
      Fetching Data…
    </LoaderWithText>

    <template v-else>
      <Level id="entity-header" class="stk-mb-6">
        <LevelLeft>
          <Title size="7">
            {{ entityStore.entity?.name || '' }}
          </Title>
          <Subtitle v-if="entityStore.entity?.email" class="email" size="5">
            <a :href="`mailto:${entityStore.entity.email}`">
              {{ entityStore.entity.email }}
            </a>
          </Subtitle>
        </LevelLeft>

        <LevelRight>
          <Dropdown
            anchor="right"
            label="Manage Entity"
          >
            <Stack gap="0">
              <!-- Client Portal Actions -->
              <MenuItem
                v-if="advisorStore.featureFlags.includes('Dossier')
                  && advisorStore.featureFlags.includes('ClientPortal')"
                class="portal-status-button"
                icon="fas fa-door-open"
                @click="redirectToClientPortal"
              >
                <span class="tw-inline-flex tw-items-center tw-gap-2">
                  Client Portal
                  <Badge :status="entityStore.portalStatusBadge.status">
                    {{ entityStore.portalStatusBadge.text }}
                  </Badge>
                </span>
              </MenuItem>

              <MenuItem
                v-if="entityStore.entity
                  && entityStore.entity.entity_type === 'Prospect'
                  && entityStore.canChangeEntity"
                icon="fas fa-user-check"
                @click="openConvertModal"
              >
                Convert to Client
              </MenuItem>

              <MenuItem
                v-if="entityStore.canChangeEntity"
                icon="fas fa-compress-arrows-alt"
                title="Merge into another entity"
                @click="openMergeModal"
              >
                <span>Merge</span>
              </MenuItem>

              <MenuItem
                v-if="advisorStore.hasFeature('FAFN Direct Indexing')"
                icon="fas fa-network-wired"
                :href="`/advisor-dashboard/direct-indexing/${entityStore.entity?.linked_entity_external_id}`"
                target="_blank"
                title="Values-Aligned Direct Index Solution"
              >
                <span>
                  {{
                    entityStore.entity?.metadata?.fafn_dndx
                      ? 'View VADIS Workflow'
                      : 'Create VADIS Workflow'
                  }}
                </span>
              </MenuItem>

              <MenuItem
                v-if="entityStore.canChangeEntity"
                icon="fas fa-edit"
                @click="openEditModal"
              >
                <span>Edit Name</span>
              </MenuItem>

              <StakeTooltip
                label="Cannot delete this client because it is synced from your custodian"
                position="left"
                trigger="hover"
                :active="!!entityStore.entity?.holdings_synced"
                class="tw-w-full tw-flex"
              >
                <MenuItem
                  v-if="entityStore.canChangeEntity"
                  :disabled="!!entityStore.entity?.holdings_synced"
                  icon="fas fa-trash"
                  variant="danger"
                  outlined
                  class="tw-full tw-grow"
                  @click="openDeleteModal"
                >
                  Delete Entity
                </MenuItem>
              </StakeTooltip>
            </Stack>
          </Dropdown>
        </LevelRight>
      </Level>

      <div id="entity-scores" class="tw-flex tw-justify-between tw-items-start tw-mb-4">
        <!-- Left: Risk columns (unchanged) -->
        <div class="tw-flex tw-gap-8">
          <!-- Client Risk -->
          <div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <strong>Client Risk Level:</strong>
              <Badge v-if="clientRiskScore !== null" status="primary">
                {{ `${clientRiskScore} (${ riskMapping(clientRiskScore).level })`}}
              </Badge>
              <span v-else>—</span>
            </div>
            <p class="tw-flex">
              <router-link
                class="is-primary stk-size-3"
                :to="{ name: 'entity-reports' }"
                replace
              >
                {{ clientRiskScore !== null ? 'View / Change' : 'Add / Update' }}
              </router-link>
            </p>
          </div>

          <!-- Portfolio Risk -->
          <div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <strong>Portfolio Risk Score:</strong>

              <!-- spinner while fetching -->
              <span v-if="isFetchingPortfolioRisk">
                <i class="fa fa-spinner fa-spin"></i>
              </span>

              <!-- once fetched, show badge or dash -->
              <Badge v-else-if="portfolioRiskScore !== null" status="primary">
                {{ `${Math.round(portfolioRiskScore)} (${ riskMapping(portfolioRiskScore).level })` }}
              </Badge>
              <span v-else>—</span>
              <span
                v-if="portfolioRiskScore !== null && clientRiskScore !== null && Math.abs(portfolioRiskScore - clientRiskScore) > 10"
                class="tw-text-red-500 tw-cursor-pointer"
                title="The portfolio risk score differs significantly from the client risk level."
              >
                <i class="fas fa-exclamation-circle"></i>
              </span>
            </div>
            <p class="tw-flex">
              <router-link
                v-if="!isFetchingPortfolioRisk && portfolioRiskScore === null"
                replace
                class="is-primary stk-size-3"
                :to="{ name: 'entity-portfolio' }"
              >
                Add holdings
              </router-link>
            </p>
          </div>
        </div>

        <!-- Right: Client Values as its own column -->
        <div>
          <!-- label + up to 4 badges -->
          <div class="tw-flex tw-items-center tw-gap-2">
            <strong>Client Values:</strong>
            <Badge
              v-for="val in truncatedValues"
              :key="val"
              status="success"
            >{{ val }}</Badge>
            <Badge
              v-if="extraValuesCount > 0"
              status="info"
              clickable
              :to="{ name: 'entity-reports' }"
              replace
            >
              +{{ extraValuesCount }} more
            </Badge>
          </div>
          <p class="tw-flex">
            <router-link
              class="is-primary stk-size-3"
              :to="{ name: 'entity-reports' }"
              replace
            >
              Change
            </router-link>
          </p>
        </div>
      </div>

      <template v-if="entityStore.hasPermission">
        <Pills
          id="entity-navigation-links"
          class="entity-navigation-links"
        >
          <PillsList>
            <PillItem
              v-for="link in navigationLinks"
              :key="link.to.name"
              :to="link.to"
              :icon="link.icon"
              replace
            >
              {{ link.label }}
            </PillItem>
          </PillsList>
        </Pills>

    

        <router-view
          :entity-external-id="entityExternalId"
          :entity="entityStore.entity"
          @refetch-entity="refetchEntityAndRisk"
        ></router-view>

        <StakeModal v-model="showEditModal">
          <!-- wrap in a form that prevents normal POST and calls your save method -->
          <form @submit.prevent="saveEntityDetails">
            <Stack class="stk-p-2" style="width: 32rem;" gap="4">
              <Title>Edit Name</Title>

              <Field>
                <Label>First Name</Label>
                <!-- now Enter here will submit the form -->
                <input
                  class="stk-input"
                  type="text"
                  v-model="editedFirstName"
                />
                <p v-if="editSaveError" class="help is-danger">{{ editSaveError }}</p>
              </Field>

              <Field>
                <Label>Last Name</Label>
                <input
                  class="stk-input"
                  type="text"
                  v-model="editedLastName"
                />
              </Field>

              <Buttons class="tw-justify-between">
                <!-- make this a true submit button -->
                <Button
                  type="submit"
                  variant="success"
                  :disabled="!editedFirstName"
                >
                  Save Changes
                </Button>
                <Button @click="cancelEditModal" type="button">Cancel</Button>
              </Buttons>
            </Stack>
          </form>
        </StakeModal>

        <!-- Merge Entities Modal -->
        <StakeModal v-model="showMergeModal">
          <Stack class="stk-p-2 merge-modal" style="width: 40rem;" gap="4">
            <!-- View 1: Search and select target entity -->
            <template v-if="mergeStep === 1">
              <Title>Merge Into Another Entity</Title>
              <p class="tw-text-sm tw-text-gray-600">
                Select a destination entity. All supported data on this entity will be moved into the selected entity.
              </p>

              <Field>
                <Label>Search</Label>
                <input
                  class="stk-input"
                  type="text"
                  v-model="entitySearchQuery"
                  placeholder="Name or email"
                  @input="onSearchInput"
                />
              </Field>

              <div v-if="isSearching" class="tw-text-gray-600 tw-text-sm">
                <i class="fa fa-spinner fa-spin"></i> Searching…
              </div>
              <div v-else-if="entitySearchQuery && searchResults.length === 0" class="tw-text-gray-600 tw-text-sm">
                No entities found.
              </div>

              <div v-if="searchResults.length > 0" class="tw-border tw-border-gray-200 tw-rounded tw-overflow-hidden">
                <div
                  v-for="item in searchResults"
                  class="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-px-3 tw-py-2 tw-border-b tw-border-gray-100"
                >
                  <div class="tw-flex tw-flex-col">
                    <strong class="tw-text-sm">{{ item.name || '(unnamed)' }}</strong>
                    <span class="tw-text-xs tw-text-gray-600">{{ item.email || '' }}</span>
                  </div>
                  <div>
                    <Button
                      size="3_5"
                      variant="primary"
                      :disabled="item.external_id === entityExternalId"
                      @click="selectMergeTarget(item)"
                    >
                      Select
                    </Button>
                  </div>
                </div>
              </div>

              <Buttons class="tw-justify-between tw-mt-2">
                <Button @click="closeMergeModal" type="button">Cancel</Button>
                <Button variant="success" :disabled="true">Next</Button>
              </Buttons>
            </template>

            <!-- View 2: Metadata resolution -->
            <template v-else-if="mergeStep === 2">
              <Title>Resolve VADIS Info</Title>
              <div v-if="isStepLoading" class="tw-text-gray-600 tw-text-sm tw-mt-1">
                <i class="fa fa-spinner fa-spin"></i> Loading…
              </div>
              <p class="tw-text-sm tw-text-gray-600">
                Choose which entity's VADIS info to keep on the destination.
              </p>
              <Field>
                <Label>VADIS Info to keep</Label>
                <div class="tw-flex tw-flex-col tw-gap-2">
                  <label class="tw-flex tw-items-start tw-gap-2">
                    <input type="radio" value="source" v-model="metadataChoice" />
                    <div>
                      <div>{{ entityStore.entity?.name || entityExternalId }}</div>
                      <div class="tw-text-xs tw-text-gray-700 tw-mt-1">
                        <div><strong>Universe</strong>: {{ getUniverseText(entityStore.entity && entityStore.entity.metadata) }}</div>
                        <div><strong>Values Set</strong>: {{ getValuesSetText(entityStore.entity) }}</div>
                        <div><strong>Comments</strong>: {{ getCommentsText(entityStore.entity && entityStore.entity.metadata) }}</div>
                        <div><strong>Inclusions</strong>: {{ getInclusionsText(entityStore.entity && entityStore.entity.metadata) }}</div>
                        <div><strong>Exclusions</strong>: {{ getExclusionsText(entityStore.entity && entityStore.entity.metadata) }}</div>
                        <div><strong>Short Term Gains Budget</strong>: {{ getBudgetText(entityStore.entity && entityStore.entity.metadata, 'short_term_gains_budget') }}</div>
                        <div><strong>Long Term Gains Budget</strong>: {{ getBudgetText(entityStore.entity && entityStore.entity.metadata, 'long_term_gains_budget') }}</div>
                        <div><strong>Tax Loss Harvesting Authorized</strong>: {{ getTlhAuthorizedText(entityStore.entity && entityStore.entity.metadata) }}</div>
                        <div class="tw-mt-1"><strong>Accounts</strong>:</div>
                        <div v-for="line in getAccountsSummary(entityStore.entity && entityStore.entity.metadata)">{{ line }}</div>
                      </div>
                    </div>
                  </label>
                  <label class="tw-flex tw-items-start tw-gap-2">
                    <input type="radio" value="target" v-model="metadataChoice" />
                    <div>
                      <div>{{ (selectedMergeTargetFull && (selectedMergeTargetFull.name || selectedMergeTargetFull.external_id)) || (selectedMergeTarget && (selectedMergeTarget.name || selectedMergeTarget.external_id)) }}</div>
                      <div class="tw-text-xs tw-text-gray-700 tw-mt-1">
                        <div><strong>Universe</strong>: {{ getUniverseText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata)) }}</div>
                        <div><strong>Values Set</strong>: {{ getValuesSetText(selectedMergeTargetFull || selectedMergeTarget) }}</div>
                        <div><strong>Comments</strong>: {{ getCommentsText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata)) }}</div>
                        <div><strong>Inclusions</strong>: {{ getInclusionsText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata)) }}</div>
                        <div><strong>Exclusions</strong>: {{ getExclusionsText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata)) }}</div>
                        <div><strong>Short Term Gains Budget</strong>: {{ getBudgetText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata), 'short_term_gains_budget') }}</div>
                        <div><strong>Long Term Gains Budget</strong>: {{ getBudgetText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata), 'long_term_gains_budget') }}</div>
                        <div><strong>Tax Loss Harvesting Authorized</strong>: {{ getTlhAuthorizedText((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata)) }}</div>
                        <div class="tw-mt-1"><strong>Accounts</strong>:</div>
                        <div v-for="line in getAccountsSummary((selectedMergeTargetFull && selectedMergeTargetFull.metadata) || (selectedMergeTarget && selectedMergeTarget.metadata))">{{ line }}</div>
                      </div>
                    </div>
                  </label>
                </div>
              </Field>

              <Callout v-if="mergeError" status="danger">{{ mergeError }}</Callout>

              <Buttons class="tw-justify-between tw-mt-2">
                <Button @click="closeMergeModal" type="button">Cancel</Button>
                <Button
                  variant="success"
                  :disabled="!metadataChoice"
                  @click="goToPreview"
                >
                  Continue
                </Button>
              </Buttons>
            </template>

            <!-- View 3: Preview -->
            <template v-else-if="mergeStep === 3">
              <Title>Preview Merge</Title>
              <!-- Dossier draft warnings -->
              <div class="tw-space-y-2 tw-mb-3">
                <Callout
                  v-if="hasUnreviewedSourceDossier"
                  status="warning"
                >
                  We recommend reviewing pending updates to the dossier for <strong>{{ entityStore.entity?.name || entityExternalId }}</strong> before merging.
                  You can review the updates
                  <a
                    :href="$router && $router.resolve({ name: 'reconciliation', params: { entityExternalId } }).href"
                    target="_blank"
                    rel="noopener"
                  >here</a>. Please go review and then you can come back to this page and merge.
                </Callout>
                <Callout
                  v-if="hasUnreviewedTargetDossier"
                  status="warning"
                >
                  We recommend reviewing pending updates to the dossier for <strong>{{ (selectedMergeTarget && (selectedMergeTarget.name || selectedMergeTarget.external_id)) || '' }}</strong> before merging.
                  You can review the updates
                  <a
                    v-if="selectedMergeTarget"
                    :href="$router && $router.resolve({ name: 'reconciliation', params: { entityExternalId: selectedMergeTarget.external_id } }).href"
                    target="_blank"
                    rel="noopener"
                  >here</a>. Please go review and then you can come back to this page and merge.
                </Callout>
              </div>
              <div class="tw-text-sm tw-text-gray-700">
                Selected target: <strong>{{ selectedMergeTarget?.name || selectedMergeTarget?.external_id }}</strong>
              </div>
              <div class="tw-text-sm tw-text-gray-600"
                v-if="isNonEmptyObject(selectedMergeTargetFull && selectedMergeTargetFull.metadata) && isNonEmptyObject(entityStore.entity && entityStore.entity.metadata)"
              >
                VADIS choice: <strong>{{ metadataChoice === 'source' ? (entityStore.entity?.name || entityExternalId) : ((selectedMergeTargetFull && (selectedMergeTargetFull.name || selectedMergeTargetFull.external_id)) || (selectedMergeTarget && (selectedMergeTarget.name || selectedMergeTarget.external_id))) }}</strong>
              </div>
              <div
                v-if="bothHaveValues"
                class="tw-text-sm tw-text-gray-700 tw-mt-1"
              >
                {{ valuesChoiceText }}
              </div>
              <div class="tw-mt-3">
                <div v-if="hasItemsToMove" class="tw-flex tw-items-center tw-justify-between tw-flex-wrap">
                  <strong>Preview Changes</strong>
                </div>
                <div v-if="isPreviewing" class="tw-text-gray-600 tw-text-sm tw-mt-1">
                  Generating preview…
                </div>
                <div v-if="hasItemsToMove" class="tw-mt-2 tw-space-y-2">
                  <div class="tw-text-sm tw-text-gray-700">
                    This will move:
                  </div>
                  <ul class="tw-list-disc tw-ml-6 tw-text-sm">
                    <li v-if="preview.holdings_moved">Holdings: {{ preview.holdings_moved }}</li>
                    <li v-if="preview.questionnaires_relinked">Questionnaires: {{ preview.questionnaires_relinked }}</li>
                    <li v-if="preview.meetings_relinked">Meetings: {{ preview.meetings_relinked }}</li>
                    <li v-if="preview.meeting_primers_relinked">Meeting Primers: {{ preview.meeting_primers_relinked }}</li>
                    <li v-if="preview.notes_relinked">Notes: {{ preview.notes_relinked }}</li>
                    <li v-if="preview.tasks_relinked">Tasks: {{ preview.tasks_relinked }}</li>
                    <li v-if="preview.checklist_item_statuses_relinked">Checklist Items: {{ preview.checklist_item_statuses_relinked }}</li>
                    <li
                      v-if="preview.portfolio_checkups_relinked || preview.portfolio_checkups_m2m_relinked"
                    >
                      Portfolio Checkups:
                        {{ totalPortfolioCheckups }}
                    </li>
                    <li v-if="preview.values_merged">Values sets: {{ preview.values_merged }}</li>
                    <li v-if="preview.parents_children_merged">Relationships (parents/children): {{ preview.parents_children_merged }}</li>
                    <li v-if="preview.prospect_metadata_set || preview.shareowner_metadata_set">VADIS Workflows: 1</li>
                    <li v-if="preview.doc_uploads_moved">Documents: {{ preview.doc_uploads_moved }}</li>
                  </ul>
                </div>
              </div>

              <Callout v-if="mergeError" status="danger">{{ mergeError }}</Callout>

              <Buttons class="tw-justify-between tw-mt-2">
                <Button @click="closeMergeModal" type="button">Cancel</Button>
                <Button
                  variant="success"
                  :disabled="!selectedMergeTarget || isMerging"
                  @click="confirmMergeEntities"
                >
                  <span v-if="!isMerging">Confirm Merge</span>
                  <span v-else><i class="fa fa-spinner fa-spin"></i> Merging…</span>
                </Button>
              </Buttons>
            </template>

            <!-- View 4: Result -->
            <template v-else>
              <Title>Merge Result</Title>
              <Callout v-if="mergeError" status="danger">{{ mergeError }}</Callout>
              <Callout v-else status="success">
                Merged information successfully to
                <a
                  :href="$router && $router.resolve({ name: 'entity-dossier', params: { entityExternalId: selectedMergeTarget && selectedMergeTarget.external_id } }).href"
                >
                  {{ (selectedMergeTarget && (selectedMergeTarget.name || selectedMergeTarget.external_id)) || 'target' }}
                </a>.
              </Callout>
              <Buttons class="tw-justify-end tw-mt-2">
                <Button @click="closeMergeModal" type="button">Close</Button>
              </Buttons>
            </template>
          </Stack>
        </StakeModal>

        <StakeModal v-model="showDeleteModal">
          <DeleteEntityForm
            :entity="entityStore?.entity"
            :entity-external-id="entityExternalId"
            :headers="headers"
          />
        </StakeModal>
        <StakeModal v-model="showConvertModal">
          <Stack class="stk-p-4" gap="4" style="width: 32rem;">
            <Title>Convert Prospect to Client</Title>
            <Callout status="warning" class="tw-mb-2" v-if="!convertWouldDuplicate">
              This action cannot be undone. You cannot convert back to a Prospect.
            </Callout>
            <div v-if="convertWouldDuplicate" class="tw-mt-2">
              <Badge status="danger">It looks like you already have a client with this email address. Please reach out to support@yourstake.org if you need help.</Badge>
            </div>
            <div v-if="convertError" class="tw-text-red-600 tw-mt-2">{{ convertError }}</div>
            <Buttons class="tw-justify-between tw-mt-4">
              <Button
                v-if="!convertWouldDuplicate"
                variant="primary"
                :disabled="isConverting"
                @click="confirmConvert"
              >
                {{ isConverting ? 'Converting…' : 'Confirm Conversion' }}
              </Button>
              <Button 
                variant="danger"
                @click="showConvertModal = false"
              >
                Cancel
              </Button>
            </Buttons>
          </Stack>
        </StakeModal>
      </template>

      <Callout
        v-else
        status="danger"
      >
        You do not have permission to view this entity.
      </Callout>
    </template>

    <!-- Chat Panel (initially hidden) -->
  </div>
</template>

<script>
import MeetingPrep from './MeetingPrep.vue';
import DocMasterAdvisor from './DocMasterViewAdvisor.vue';
import Dossier from './Dossier.vue';
import PermissionsGroups from './PermissionsGroups.vue';
import EntityPermissions from './EntityPermissions.vue';
import EntityReports from './EntityReports.vue';
import NoteTaking from './NoteTaking.vue';
import OAuthIntegrationDashboard from '@frontend/ui/features/oauth/views/OAuthIntegrationDashboard.vue';
import EntityChat from './EntityChat.vue';
import EntityHoldings from './EntityHoldings.vue';
import EntityRecommendations from './EntityRecommendations.vue';
import EntityRecommendationsConsiderations from './EntityRecommendationsConsiderations.vue';
import EntityPlanning from './planning/EntityPlanning.vue';
import {
  Badge,
  Badges,
  Buttons,
  Button,
  Callout,
  Dropdown,
  Field,
  Label,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  MenuItem,
  Pills,
  PillsList,
  PillItem,
  Stack,
  StakeModal,
  Subtitle,
  Title,
  StakeTooltip,
} from '@stakeui';
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import DeleteEntityForm from '../components/DeleteEntityForm.vue';
import { useEntityStore } from '@frontend/ui/stores/entityStore';

export default {
  name: 'EntityDetail',
  components: {
    MeetingPrep,
    DocMasterAdvisor,
    Dossier,
    EntityChat,
    PermissionsGroups,
    EntityPermissions,
    EntityReports,
    NoteTaking,
    EntityHoldings,
    EntityRecommendations,
    EntityRecommendationsConsiderations,
    EntityPlanning,
    OAuthIntegrationDashboard,
    DeleteEntityForm,
    Badge,
    Badges,
    Buttons,
    Button,
    Callout,
    Dropdown,
    Field,
    Label,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    MenuItem,
    Pills,
    PillsList,
    PillItem,
    Stack,
    StakeModal,
    Subtitle,
    Title,
    StakeTooltip,
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      // We're not at the top-level entity route,
      // which means we're directly heading to one of the tabs,
      // so we're just proceeding
      if (vm.$route.name !== 'entity-detail') {
        return;
      }

      vm.$router.replace({ name: 'entity-summary' });
    })
  },
  beforeRouteUpdate(to, from, next) {
    this.showCrmLinkingModal = false;
    this.showPortalDrawer = false;
    next();
  },
  beforeRouteLeave(to, from, next) {
    // Clear portfolios before leaving Entity Detail page
    this.checkupStore.portfolios = [];
    this.checkupStore.resetState();
    this.entityStore.clearEntity();

    next(); // Continue to desired route
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    meetingId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      showCrmLinkingModal: false,
      showEditModal: false,
      showDeleteModal: false,
      showConvertModal: false,
      showInviteModal: false,
      showPortalDrawer: false,
      editedFirstName: '',
      editedLastName: '',
      editSaveError: '',
      portfolioRiskScore: null,
      isFetchingPortfolioRisk: false,
      showChat: false,
      portalStatus: null,
      // Merge UI state
      showMergeModal: false,
      mergeStep: 1,
      entitySearchQuery: '',
      isSearching: false,
      searchResults: [],
      selectedMergeTarget: null,
      selectedMergeTargetFull: null,
      metadataChoice: null,
      isMerging: false,
      mergeError: '',
      mergeSuccess: '',
      searchDebounceTimer: null,
      // preview
      isPreviewing: false,
      preview: null,
      isStepLoading: false,
      isConverting: false,
      convertWouldDuplicate: false,
      convertError: '',
      // dossier draft flags
      hasUnreviewedSourceDossier: false,
      hasUnreviewedTargetDossier: false,
    };
  },
  computed: {
    ...mapStores(useAdvisorStore, useEntityStore, useCheckupStore),
    navigationLinks() {
      const items = [
        {
          position: 5,
          label: "Summary",
          icon: "fas fa-clipboard-list",
          to: { name: "entity-summary" },
        },
        {
          position: 20,
          label: "Portfolio",
          icon: "fas fa-chart-pie",
          to: { name: "entity-portfolio" },
        },
        {
          position: 30,
          label: "Reports",
          icon: "fas fa-file-alt",
          to: { name: "entity-reports" },
        },
        {
          position: 70,
          label: "Groups",
          icon: "fas fa-users",
          to: { name: "entity-groups" },
        },
        // {
        //   position: 80,
        //   label: "Permissions",
        //   icon: "fas fa-lock",
        //   to: { name: "entity-permissions" },
        // },
      ];

      if (this.advisorStore.featureFlags.includes('Dossier')) {
        items.push(
          {
            position: 10,
            label: "Dossier",
            icon: "fas fa-book",
            to: { name: "entity-dossier" },
          },
          {
            position: 40,
            label: "Documents",
            icon: "fas fa-folder-open",
            to: { name: "entity-documents" },
          },
          {
            position: 50,
            label: "Recommendations",
            icon: "fas fa-star",
            to: { name: "entity-recommendations" },
          },
        )
      }
      if (this.advisorStore.featureFlags.includes('Entity Planning')) {
        items.push(
          {
            position: 60,
            label: "Planning",
            icon: "fas fa-chart-line",
            to: { name: "entity-planning" },
          }
        );
      }

      if (this.advisorStore.featureFlags.includes('Meetings')) {
        items.push(
          {
            position: 50,
            label: "Meetings",
            icon: "fas fa-align-left",
            to: { name: "entity-note-taking" },
          }
        );
      }

      items.push(
        {
          position: 50,
          label: "Notes",
          icon: "fas fa-sticky-note",
          to: { name: "entity-simple-notes" },
        }
      );

      return items.sort((a, b) => a.position - b.position);;
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    clientRiskScore() {
      return this.entityStore.entity?.client_risk_score ?? null;
    },
    clientValues() {
      return this.entityStore.entity?.screen_sets?.map(s => s.name) || [];
    },
    truncatedValues() {
      return this.clientValues.slice(0, 2);
    },
    extraValuesCount() {
      return Math.max(0, this.clientValues.length - 2);
    },
    portfolioValuesAlignment() {
      return this.entityStore.entity?.values_alignment_score ?? null;
    },
    sourceVadisKeysText() {
      const meta = this.entityStore.entity && this.entityStore.entity.metadata;
      const keys = this.getVadisKeys(meta);
      return keys.length ? `Fields: ${keys.join(', ')}` : '';
    },
    targetVadisKeysText() {
      const meta = (this.selectedMergeTargetFull && this.selectedMergeTargetFull.metadata) || (this.selectedMergeTarget && this.selectedMergeTarget.metadata);
      const keys = this.getVadisKeys(meta);
      return keys.length ? `Fields: ${keys.join(', ')}` : '';
    },
    sourceHasValues() {
      try {
        const sets = (this.entityStore.entity && this.entityStore.entity.screen_sets) || [];
        return Array.isArray(sets) && sets.length > 0;
      } catch (_) {
        return false;
      }
    },
    targetHasValues() {
      try {
        const tgt = this.selectedMergeTargetFull || this.selectedMergeTarget || {};
        const sets = tgt.screen_sets || [];
        return Array.isArray(sets) && sets.length > 0;
      } catch (_) {
        return false;
      }
    },
    bothHaveValues() {
      return this.sourceHasValues && this.targetHasValues;
    },
    selectedValuesOwner() {
      if (!this.bothHaveValues) return null;
      if (this.metadataChoice === 'source') return 'source';
      if (this.metadataChoice === 'target') return 'target';
      // Default behavior when no explicit VADIS selection:
      // keep destination (target) values unless source has VADIS and target does not
      const sourceHasVadis = this.getVadisFlag(this.entityStore.entity && this.entityStore.entity.metadata);
      const targetMeta = (this.selectedMergeTargetFull && this.selectedMergeTargetFull.metadata) || (this.selectedMergeTarget && this.selectedMergeTarget.metadata);
      const targetHasVadis = this.getVadisFlag(targetMeta);
      if (sourceHasVadis && !targetHasVadis) return 'source';
      return 'target';
    },
    valuesChoiceText() {
      if (!this.bothHaveValues) return '';
      const sourceName = this.entityStore.entity?.name || this.entityExternalId;
      const targetName = (this.selectedMergeTargetFull && (this.selectedMergeTargetFull.name || this.selectedMergeTargetFull.external_id)) ||
                         (this.selectedMergeTarget && (this.selectedMergeTarget.name || this.selectedMergeTarget.external_id)) ||
                         '';
      const ownerName = this.selectedValuesOwner === 'source' ? sourceName : targetName;
      const reason = this.metadataChoice
        ? 'based on the selected VADIS workflow'
        : (this.selectedValuesOwner === 'target'
            ? 'because merges keep the destination’s values'
            : 'because the source has a VADIS workflow and the destination does not');
      return `Both entities have client values. This merge will use ${ownerName}'s values (${reason}).`;
    },
    hasItemsToMove() {
      if (!this.preview) return false;
      const itemsToCheck = [
        'holdings_moved',
        'questionnaires_relinked',
        'meetings_relinked',
        'meeting_primers_relinked',
        'notes_relinked',
        'tasks_relinked',
        'checklist_item_statuses_relinked',
        'portfolio_checkups_relinked',
        'portfolio_checkups_m2m_relinked',
        'values_merged',
        'parents_children_merged',
        'prospect_metadata_set',
        'shareowner_metadata_set',
        'doc_uploads_moved'
      ];
      return itemsToCheck.some(item => this.preview[item] && this.preview[item] > 0);
    },
    totalPortfolioCheckups() {
      if (!this.preview) return 0;
      const fkCheckups = this.preview.portfolio_checkups_relinked || 0;
      const m2mCheckups = this.preview.portfolio_checkups_m2m_relinked || 0;
      return fkCheckups + m2mCheckups;
    },
  },
  async mounted() {
    await this.fetchEntity();
    await this.fetchPortfolioRiskScore();
    
    // Show drawer by default for advisors with proper permissions and feature flags
    this.showPortalDrawer = this.entityStore.hasPermission && 
                           this.advisorStore.featureFlags.includes('Dossier') && 
                           this.advisorStore.featureFlags.includes('ClientPortal');
  },
  methods: {
    async fetchMeetings() {
      try {
        if (this.meetingId) {
          return;
        }
        const response = await fetch(`/api/v1/meetings/entity/${this.$route.params.entityExternalId}/`);
        const data = await response.json();
        if (data.meetings && data.meetings.length > 0) {
          // Set the most recent meeting ID
          this.meetingId = data.meetings[0].id;
        } else {
          this.meetingId = 'new';
        }
      } catch (error) {
        console.error('Error fetching meetings:', error);
      }
    },
    async fetchPortfolioRiskScore() {
      this.isFetchingPortfolioRisk = true;
      try {
        const res = await fetch(`/api/v1/get-entity-portfolio-risk-score/${this.entityExternalId}/`)
        const json = await res.json();
        this.portfolioRiskScore = json.risk_score;
      } catch (err) {
        console.error('Failed loading portfolio risk:', err);
        this.portfolioRiskScore = null;
      } finally {
        this.isFetchingPortfolioRisk = false;
      }
    },

    async fetchEntity() {
      await this.entityStore.fetchEntity(this.$route.params.entityExternalId);
      // Entity is ready. CRM is not but we can already display the UI.
      this.loading = false;
      await this.entityStore.fetchCrmContact();
    },
    async refetchEntityAndRisk() {
      // refresh whether the entity has holdings boolean
      // After holdings are populated, fetch the portfolio risk score
      await this.fetchEntity();
      await this.fetchPortfolioRiskScore();
    },
    async handleCrmContactLinked(data) {
      // Update the entity data after linking
      await this.fetchEntity();
    },
    async handleInvitationLinkGenerated(data) {
      // Update the entity to reflect portal access
      await this.fetchEntity();
      // Keep the modal open so user can see and copy the link
    },
    riskMapping(score) {
      const mappings = {
        '0-20':   { level: 'Conservative',            description: 'You prefer to keep your investments safe, even if it means potentially lower returns.' },
        '21-40':  { level: 'Moderately Conservative', description: 'You are willing to take on a little risk for some potential reward.' },
        '41-60':  { level: 'Moderate',                 description: 'You are comfortable with a moderate level of risk for moderate potential returns.' },
        '61-80':  { level: 'Moderately Aggressive',    description: 'You are willing to take on significant risk for higher potential returns.' },
        '81-100': { level: 'Aggressive',               description: 'You are willing to take on more risk for a potential higher reward, and you\'re okay with the volatility that comes with it.' },
      };

      for (const range in mappings) {
        const [min, max] = range.split('-').map(Number);
        if (score >= min && score <= max) {
          return mappings[range];
        }
      }

      return {
        level: 'Unknown',
        description: 'Risk score is not within expected range.'
      };
    },
    openEditModal() {
      const full = this.entityStore.entity?.name || '';
      const idx = full.lastIndexOf(' ');
      if (idx > 0) {
        this.editedFirstName = full.slice(0, idx);
        this.editedLastName  = full.slice(idx + 1);
      } else {
        // no space → put it all in first name
        this.editedFirstName = full;
        this.editedLastName  = '';
      }
      this.editSaveError = '';
      this.showEditModal  = true;
    },
    openDeleteModal() {
      this.showDeleteModal = true;
    },
    async openConvertModal() {
      this.convertError = ''
      this.convertWouldDuplicate = false
      this.showConvertModal = true
      try {
        const res = await fetch(`/api/v1/entities/${this.entityExternalId}/convert-to-shareowner/status/`)
        if (res.ok) {
          const json = await res.json()
          this.convertWouldDuplicate = !!json.would_duplicate
        }
      } catch (e) {
        // No-op; still allow user to proceed
      }
    },
    async confirmConvert() {
      this.isConverting = true
      this.convertError = ''
      try {
        const res = await fetch(`/api/v1/entities/${this.entityExternalId}/convert-to-shareowner/`, {
          method: 'POST',
          headers: {
            ...this.headers,
          },
        })
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Conversion failed.' }))
          this.convertError = err.error || 'Conversion failed.'
          return
        }
        await this.fetchEntity()
        this.showConvertModal = false
      } catch (e) {
        this.convertError = 'Conversion failed.'
      } finally {
        this.isConverting = false
      }
    },
    cancelEditModal() {
      this.showEditModal = false;
    },
    async saveEntityDetails() {
      // 1) validation
      if (!this.editedFirstName) {
        this.editSaveError = 'First name is required.'
        return
      }

      try {
        // 2) call your update-entity-field endpoint
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`
        const res = await fetch(url, {
          method: 'POST',
          headers: { 
            ...this.headers, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({
            first_name: this.editedFirstName,
            last_name:  this.editedLastName
          })
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.error || 'Update failed.')
        }

        // 3) reflect the change immediately
        this.entityStore.updateName(this.editedFirstName, this.editedLastName);
        this.showEditModal = false;

      } catch (e) {
        console.error('Error saving entity details:', e)
        this.editSaveError = e.message || 'Failed to save changes.'
      }
    },
    openMergeModal() {
      this.showMergeModal = true;
      this.mergeStep = 1;
      this.entitySearchQuery = '';
      this.searchResults = [];
      this.selectedMergeTarget = null;
      this.selectedMergeTargetFull = null;
      this.metadataChoice = null;
      this.mergeError = '';
      this.mergeSuccess = '';
      this.hasUnreviewedTargetDossier = false;
      // pre-check source dossier draft status
      this.checkDossierDraftStatus(this.entityExternalId).then(val => {
        this.hasUnreviewedSourceDossier = val;
      }).catch(() => {
        this.hasUnreviewedSourceDossier = false;
      });
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchEntities();
      }, 300);
    },
    async searchEntities() {
      try {
        this.isSearching = true;
        this.mergeError = '';
        const params = new URLSearchParams();
        if (this.entitySearchQuery) params.set('search', this.entitySearchQuery);
        params.set('page_size', '10');
        const resp = await fetch(`/api/v1/entities/get_all/?${params.toString()}`);
        const json = await resp.json();
        this.searchResults = (json.items || []).filter(it => it.external_id !== this.entityExternalId);
      } catch (e) {
        console.error('Search failed', e);
        this.mergeError = 'Search failed';
      } finally {
        this.isSearching = false;
      }
    },
    async selectMergeTarget(item) {
      this.isStepLoading = true;
      try {
        this.selectedMergeTarget = item;
        this.mergeError = '';
        this.mergeSuccess = '';
        this.preview = null;
        // fetch full target details to inspect metadata
        this.selectedMergeTargetFull = await this.fetchEntityByExternalId(item.external_id);
        // check target's dossier draft status
        try {
          this.hasUnreviewedTargetDossier = await this.checkDossierDraftStatus(item.external_id);
        } catch (_) {
          this.hasUnreviewedTargetDossier = false;
        }
        const sourceHas = this.isNonEmptyObject(this.entityStore.entity && this.entityStore.entity.metadata);
        const targetHas = this.isNonEmptyObject(this.selectedMergeTargetFull && this.selectedMergeTargetFull.metadata);
        console.error('THIS Entity Metadata')
        console.error(this.entityStore.entity.metadata)
        console.error('TARGET Entity Metadata')
        console.error(this.selectedMergeTargetFull.metadata)
        if (sourceHas && targetHas) {
          this.mergeStep = 2;
        } else {
          // auto choose the non-empty side (or target if both empty)
          this.metadataChoice = sourceHas ? 'source' : 'target';
          this.mergeStep = 3;
          await this.previewMerge();
        }
      } catch (e) {
        console.error('Select target failed', e);
        this.mergeError = 'Failed to load selected entity. Please try again.';
      } finally {
        this.isStepLoading = false;
      }
    },
    async confirmMergeEntities() {
      if (!this.selectedMergeTarget) return;
      try {
        this.isMerging = true;
        this.mergeError = '';
        this.mergeSuccess = '';
        const resp = await fetch('/api/v1/entities/merge/', {
          method: 'POST',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            source_entity_external_id: this.entityExternalId,
            target_entity_external_id: this.selectedMergeTarget.external_id,
            metadata_choice: this.metadataChoice || null,
          }),
        });
        if (!resp.ok) {
          let errText = 'Merge failed';
          try {
            const errJson = await resp.json();
            errText = errJson.error || errText;
          } catch (_) {}
          throw new Error(errText);
        }
        const json = await resp.json();
        this.mergeSuccess = 'Merged successfully.';
        // keep a snapshot of what moved for user to review
        this.preview = json;
        // optional: refresh current entity view
        await this.fetchEntity();
        this.mergeStep = 4;
      } catch (e) {
        console.error('Merge error', e);
        this.mergeError = e.message || 'Merge failed';
        this.mergeStep = 4;
      } finally {
        this.isMerging = false;
      }
    },
    async previewMerge() {
      if (!this.selectedMergeTarget) return;
      try {
        this.isPreviewing = true;
        this.preview = null;
        const resp = await fetch('/api/v1/entities/merge/', {
          method: 'POST',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            source_entity_external_id: this.entityExternalId,
            target_entity_external_id: this.selectedMergeTarget.external_id,
            dry_run: true,
            detail: true,
            limit: 5,
            metadata_choice: this.metadataChoice || null,
          }),
        });
        if (!resp.ok) {
          return;
        }
        const json = await resp.json();
        this.preview = json;
      } catch (_) {
        // swallow
      } finally {
        this.isPreviewing = false;
      }
    },
    async fetchEntityByExternalId(externalId) {
      const resp = await fetch(`/api/v1/entities/${externalId}/`);
      if (!resp.ok) throw new Error('Failed to load entity');
      return await resp.json();
    },
    isNonEmptyObject(obj) {
      try {
        return !!obj && typeof obj === 'object' && Object.keys(obj).length > 0;
      } catch (_) {
        return false;
      }
    },
    metaSummary(obj) {
      return this.isNonEmptyObject(obj) ? `${Object.keys(obj).length} keys` : 'empty';
    },
    async checkDossierDraftStatus(externalId) {
      try {
        const resp = await fetch(`/api/v1/dossier/entity/${externalId}/draft/`);
        if (!resp.ok) return false;
        const json = await resp.json();
        const data = json && json.dossier_data;
        return !!(data && typeof data === 'object' && Object.keys(data).length > 0);
      } catch (_) {
        return false;
      }
    },
    async goToPreview() {
      this.mergeStep = 3;
      await this.previewMerge();
    },
    redirectToClientPortal() {
      const advisor = this.advisorStore.advisor;

      if (advisor?.firm?.slug && advisor?.slug) {
        const routeData = this.$router.resolve({
          name: 'advisor-client-portal-setup-checklist',
          params: {
            firmSlug: advisor.firm.slug,
            advisorSlug: advisor.slug,
            entityExternalId: this.entityExternalId,
          },
        });

        // Opens in a new tab like <router-link target="_blank">
        window.open(routeData.href, '_blank');
      } else {
        console.error('Advisor or firm information is not available to construct the client portal URL. The firm object is missing the "slug" property.');
        console.log('Dumping advisorStore.advisor object for debugging:', this.advisorStore.advisor);
        console.log('Dumping advisorStore.advisor object for debugging:', this.advisorStore.advisor.firm);
        alert('Could not open client portal. Required firm information is missing. Please contact support.');
      }
    },
    togglePortalDrawer() {
      this.showPortalDrawer = !this.showPortalDrawer;
    },
    toggleChat() {
      this.showChat = !this.showChat;
    },
    getVadisFlag(meta) {
      try {
        if (!meta || typeof meta !== 'object') return false;
        if (meta.fafn_dndx || meta.dndx || meta.vadis) return true;
        if (meta.workflow && (meta.workflow.fafn_dndx || meta.workflow.dndx)) return true;
        return false;
      } catch (_) {
        return false;
      }
    },
    getUniverseText(meta) {
      const fafn = this.getFafn(meta);
      const uni = fafn && fafn.universe;
      return uni && String(uni).trim() ? String(uni).trim() : 'None';
    },
    getValuesSetText(entityLike) {
      try {
        const sets = (entityLike && entityLike.screen_sets) || [];
        if (!Array.isArray(sets) || sets.length === 0) return 'None';
        const names = sets.map(s => s && s.name).filter(Boolean);
        return names.length ? names.join(', ') : 'None';
      } catch (_) {
        return 'None';
      }
    },
    getCommentsText(meta) {
      const fafn = this.getFafn(meta);
      const c = fafn && fafn.comments;
      if (!c && c !== 0) return 'None';
      const str = String(c).trim();
      return str || 'None';
    },
    getFafn(meta) {
      try {
        if (!meta || typeof meta !== 'object') return null;
        if (meta.fafn_dndx) return meta.fafn_dndx;
        if (meta.workflow && meta.workflow.fafn_dndx) return meta.workflow.fafn_dndx;
        return null;
      } catch (_) {
        return null;
      }
    },
    getInclusionsText(meta) {
      const fafn = this.getFafn(meta);
      if (!fafn) return 'None';
      const inc = fafn.inclusions;
      if (!inc) return 'None';
      if (Array.isArray(inc)) {
        return inc.length ? inc.join(', ') : 'None';
      }
      if (typeof inc === 'string') {
        const trimmed = inc.trim();
        return trimmed || 'None';
      }
      return 'None';
    },
    getExclusionsText(meta) {
      const fafn = this.getFafn(meta);
      if (!fafn) return 'None';
      const exc = fafn.exclusions;
      if (!exc) return 'None';
      if (Array.isArray(exc)) {
        return exc.length ? exc.join(', ') : 'None';
      }
      if (typeof exc === 'string') {
        const trimmed = exc.trim();
        return trimmed || 'None';
      }
      return 'None';
    },
    getBudgetText(meta, field) {
      const fafn = this.getFafn(meta);
      if (!fafn) return 'None';
      const val = fafn && fafn[field];
      if (val === undefined || val === null || val === '') return 'None';
      return String(val);
    },
    getTlhAuthorizedText(meta) {
      const fafn = this.getFafn(meta);
      if (!fafn || typeof fafn.authorize_fafn_tax_loss_harvesting === 'undefined') return 'None';
      return fafn.authorize_fafn_tax_loss_harvesting ? 'Yes' : 'No';
    },
    getAccountsSummary(meta) {
      const fafn = this.getFafn(meta);
      if (!fafn || !Array.isArray(fafn.accounts) || fafn.accounts.length === 0) {
        return ['None'];
      }
      const lines = [];
      for (const acc of fafn.accounts) {
        const namePrefix = acc && acc.name ? `${acc.name} ` : '';
        const dollarsRaw = (acc && (acc.dollars || acc.size)) || '';
        const dollars = typeof dollarsRaw === 'string'
          ? dollarsRaw.replace(/[^0-9\.]/g, '')
          : (dollarsRaw || '');
        const sizeText = dollars ? dollars : 'None';
        const custodian = (acc && acc.custodian) || 'None';
        const registration = (acc && (acc.registrationType || acc.registration)) || 'None';
        lines.push(`${namePrefix}Size: ${sizeText}, Custodian: ${custodian}, Registration: ${registration}`);
      }
      return lines;
    },
    stringifyBrief(value) {
      try {
        if (value === null || value === undefined) return 'null';
        const t = typeof value;
        if (t === 'boolean') return value ? 'true' : 'false';
        if (t === 'number') return String(value);
        if (t === 'string') return value.length > 30 ? `${value.slice(0, 27)}…` : value;
        if (Array.isArray(value)) return `array(${value.length})`;
        if (t === 'object') return `object(${Object.keys(value).length})`;
        return t;
      } catch (_) {
        return 'value';
      }
    },
    getVadisKeys(meta) {
      try {
        if (!meta || typeof meta !== 'object') return [];
        const preferred = ['fafn_dndx', 'dndx', 'vadis_version', 'last_updated'];
        const parts = [];
        for (const k of preferred) {
          if (Object.prototype.hasOwnProperty.call(meta, k)) {
            parts.push(`${k}: ${this.stringifyBrief(meta[k])}`);
          }
        }
        const rest = Object.keys(meta).filter(k => !preferred.includes(k));
        for (const k of rest.slice(0, 3)) {
          parts.push(`${k}: ${this.stringifyBrief(meta[k])}`);
        }
        return parts.slice(0, 5);
      } catch (_) {
        return [];
      }
    },
  }
};
</script>

<style scoped>
.component {
  background-color: var(--stk-scheme-main);
}

.go-back {
  color: var(--stk-text-weak);
  position: absolute;
  left: 3rem;
  top: 1rem;

  &:hover {
    color: var(--stk-primary);
  }
}

.email {
  color: var(--stk-primary);
}

.entity-navigation-links {
  font-size: 0.875em;
  padding: 0.25em;
  padding-bottom: calc(0.25em - 1px);
}

.modal-content {
  background-color: #fff;
  width: 90%;
  max-width: 60rem;
  max-height: 90vh;
  border-radius: 1em;
  overflow: auto;
}

.portal-status-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.portal-action-section {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  } 
.floating-chat-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.chat-button {
  border-radius: 2rem;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.chat-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.chat-button.active {
  background-color: #e74c3c;
}

.chat-panel {
  position: fixed;
  bottom: 6rem;
  right: 2rem;
  width: 400px;
  height: 500px;
  background: white;
  border-radius: 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 999;
  display: flex;
  flex-direction: column;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.close-chat-btn {
  padding: 0.5rem;
  min-width: unset;
}

.chat-content {
  flex: 1;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-placeholder {
  color: #666;
  font-style: italic;
}

/* Merge modal layout fixes */
.merge-modal {
  max-width: 100%;
  overflow: hidden;
}
.merge-modal .tw-flex {
  flex-wrap: wrap;
}
.merge-modal .tw-list-disc {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.merge-modal .tw-flex > * {
  min-width: 0;
}
</style>

